import { useCallback, useEffect, useState } from "react";
import "./index.css";
import {
  OrganizationResponseForAdmins,
  PaymentsService,
  StudentSubjectsService,
  StudentCreditsResponse,
} from "client/openapi";

import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import BalanceDetails from "./balanceDetails";
import CreditDetails, { getStudentCreditsTotal } from "./creditDetails";
import { Dialog, DialogTrigger } from "components/Dialog";
import {
  Button,
  ButtonColor,
  ButtonShape,
  ButtonSize,
} from "components/Button";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function Credit({
  organization,
}: {
  organization: OrganizationResponseForAdmins;
}) {
  const [creditsOwedToStudents, setCreditsOwedToStudents] = useState<number>();
  const [studentCredits, setStudentCredits] = useState<
    StudentCreditsResponse[]
  >([]);
  const [pendingBalance, setPendingBalance] = useState<number>(0);
  const [availableBalance, setAvailableBalance] = useState<number>(0);

  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();

  const getOrgBalance = useCallback(async () => {
    setStatus(PageStatus.LOADING);
    PaymentsService.getStripeBalanceForOrg({
      orgId: organization.id,
    })
      .then((balance) => {
        setAvailableBalance(balance.available_amount);
        setPendingBalance(balance.pending_amount);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((error) => {
        setError(error);
        setStatus(PageStatus.ERROR);
        console.error(`Error (#${error.status}): ${error.message}`);
      });
  }, [organization.id]);
  const getCreditsOwedToStudents = useCallback(async () => {
    setStatus(PageStatus.LOADING);
    StudentSubjectsService.getStudentCreditsByOrg({
      orgId: organization.id,
    })
      .then((credits) => {
        const sortedCredits = [...credits].sort((a, b) => {
          const nameA = `${a.student_first_name} ${a.student_last_name}`
            .trim()
            .toLowerCase();
          const nameB = `${b.student_first_name} ${b.student_last_name}`
            .trim()
            .toLowerCase();
          const nameComparison = nameA.localeCompare(nameB);

          return nameComparison !== 0
            ? nameComparison
            : a.student_id - b.student_id;
        });

        const total = sortedCredits.reduce(
          (sum, credit) => sum + getStudentCreditsTotal(credit),
          0
        );

        setStudentCredits(sortedCredits);
        setCreditsOwedToStudents(total);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((error) => {
        setError(error);
        setStatus(PageStatus.ERROR);
        console.error(`Error (#${error.status}): ${error.message}`);
      });
  }, [organization.id]);
  useEffect(() => {
    getCreditsOwedToStudents();
    getOrgBalance();
  }, [getCreditsOwedToStudents, getOrgBalance]);
  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);
  return (
    <div className="credit-container mx-2">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Dialog>
          <DialogTrigger asChild>
            <Button color={ButtonColor.DARK_BLUE} shape={ButtonShape.SQUARE}>
              <span>{`Account Balance: ${currencyFormatter.format(
                availableBalance ? availableBalance / 100 : 0
              )}`}</span>
            </Button>
          </DialogTrigger>
          <BalanceDetails
            availableBalance={availableBalance}
            pendingBalance={pendingBalance}
            organization={organization}
          />
        </Dialog>
      </div>
      <div className="pay-per-session-styling flex flex-wrap items-center gap-2 justify-between">
        <Dialog>
          <DialogTrigger asChild>
            <Button
              color={ButtonColor.PURPLE}
              shape={ButtonShape.SQUARE}
              size={ButtonSize.SMALL}
            >
              <span>
                Net Credit Balance:{" "}
                {currencyFormatter.format(
                  creditsOwedToStudents ? creditsOwedToStudents / 100 : 0
                )}
              </span>
            </Button>
          </DialogTrigger>

          <CreditDetails credits={studentCredits} />
        </Dialog>
      </div>
    </div>
  );
}
export default Credit;
